import { useGetStrings } from "@app/store/service/cmsService"
import { useTimeblocks } from "@app/store/service/connectorService"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { selectVisitorKindIds } from "@app/utils/selectors"
import { classNamez, useCallbackRef, useModal } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import TimeblockButton from "../TimeblockButton/TimeblockButton"
import TimeslotButton from "../TimeslotButton/TimeslotButton"

import RetryError from "@app/components/core/RetryError"
import NotificationInfoModal from "@app/modals/NotificationInfoModal"
import { useRaiseError } from "@app/store/actions/errorActions"
import { setSelectedTimeblockKind, setSelectedTimeslot } from "@app/store/actions/orderActions"
import { appStore } from "@app/store/store"
import { elementWasSelected } from "@app/utils/accessibility"
import { TICKET_TYPES } from "@app/utils/constants"
import { compareDate } from "@app/utils/objectUtils"
import { hasDifferentPrice } from "@app/utils/zoneUtils"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import "./BaseStack.css"

// const scrollIntoView = (parent, child) => {
// 	const parentBounding = parent.getBoundingClientRect(),
// 		clientBounding = child.getBoundingClientRect()

// 	const parentBottom = parentBounding.bottom,
// 		parentTop = parentBounding.top,
// 		clientBottom = clientBounding.bottom,
// 		clientTop = clientBounding.top

// 	if (parentTop >= clientTop) {
// 		scrollTo(parent, -(parentTop - clientTop), 300)
// 	} else if (clientBottom > parentBottom) {
// 		scrollTo(parent, clientBottom - parentBottom, 300)
// 	}
// }
// function scrollTo(element, to, duration) {
// 	let start = element.scrollTop,
// 		currentTime = 0,
// 		increment = 20

// 	let animateScroll = function () {
// 		currentTime += increment

// 		let val = easeInOutQuad(currentTime, start, to, duration)
// 		element.scrollTop = val

// 		if (currentTime < duration) {
// 			setTimeout(animateScroll, increment)
// 		}
// 	}

// 	animateScroll()
// }
// Function for smooth scroll animation with the time duration
// function easeInOutQuad(time, startPos, endPos, duration) {
// 	time /= duration / 2

// 	if (time < 1) return (endPos / 2) * time * time + startPos
// 	time--
// 	return (-endPos / 2) * (time * (time - 2) - 1) + startPos
// }

export default function BaseStack() {
	const [openingHoursObj, setOpeningHoursObj] = useState()
	const selectedDay = useSelector(state => state.order.selectedDay)
	const selectedTbKind = useSelector(state => state.order.selectedTbKind)
	const selectedTs = useSelector(state => state.order.selectedTs)
	const visitorKindIds = useSelector(selectVisitorKindIds)
	const locationId = useSelector("core.locationId")
	const zoneId = useSelector("core.zoneId")
	const raiseError = useRaiseError()
	const [getTimeblocks, { isFetching: __loadingTimeblocks, data: timeblockData, error: timeblockError }] = useTimeblocks()

	const [message, setMessage] = useState(undefined)
	const [notifications, setNotifications] = useState(undefined)
	const [shouldFocusFirstTimeslot, setShouldFocusFirstTimeslot] = useState(false)
	const loadingTimeblocks = useDelayLoading(__loadingTimeblocks, 100)
	const modal = useModal()
	const { handleRef: tickedContainerHandler, ref: tickedContainer } = useCallbackRef()

	const {
		data: {
			dateSelectionTicketTitle,
			dateSelectionTimeslotSubTitle,
			dateSelectionTimeslotTitle,
			from,
			to,
			dateSelectionYourVisitTime,
			dateSelectionLoungerOnlyWithDay,
			dateSelectionDayWithLoungerOrOther,
			dateSelectionDayWithoutLoungerOrOther,
			dateSelectionOnlyOther,
			dateSelectionOnldyDayWithLounger,
			dateSelectionOnlyDay,
			dateSelectionNoTicket,
			dateSelectionIsClosed,
			dateSelectionIsBefore,
			dateSelectionAccessDenied,
			dateSelectionEveningTicketNote,
			moreInformation,
			ticketTypeSelectionlabel,
			dateNotificationsLabel,
			timeslotSelectionLabel,
			timeblockSelectionLabel,
		},
	} = useGetStrings()

	const scrollTicketsIntoView = useCallback(() => {
		if (tickedContainer.current) {
			tickedContainer.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
			// scrollIntoView(document.querySelector(".ticketScroller").parentNode,tickedContainer.current)
			// window.parent.scrollTo(0, 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const getOpeningHours = timeBlock => {
		let temp = timeBlock
		if (temp.timeBlocks[0].timeSlots[0]) {
			if (temp.timeBlocks.length > 1) {
				temp.timeBlocks[0].timeSlots[0].timeTo =
					temp.timeBlocks[temp.timeBlocks.length - 1].timeSlots[temp.timeBlocks[temp.timeBlocks.length - 1].timeSlots.length - 1].timeTo
			} else {
				temp.timeBlocks[0].timeSlots[0].timeTo = temp.timeBlocks[0].timeSlots[temp.timeBlocks[0].timeSlots.length - 1].timeTo
			}
		}

		return temp
	}

	const handleNotificationInfoPress = notification => {
		if (!notification.memo) return

		modal.showModal(NotificationInfoModal, {
			desktopTitle: notification.notification,
			mobileTitle: notification.notification,
			desktopContent: notification.memo,
			mobileContent: notification.memo,
		})
	}

	const fetchTimeblocks = useCallback(async () => {
		try {
			await getTimeblocks({ locationId, zoneId, visitorKindIds, dateString: selectedDay.date })
				.unwrap()
				.finally(() => {
					scrollTicketsIntoView()
				})
		} catch (e) {
			raiseError(e, undefined, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDay])

	useEffect(() => {
		if (selectedDay) {
			fetchTimeblocks()
			setNotifications(selectedDay.notifications)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDay])

	useEffect(() => {
		scrollTicketsIntoView()

		// If timeBlock was chosen through keyboard, focus first timeslot
		if (shouldFocusFirstTimeslot && selectedTbKind) {
			const firstTimeSlot = document.querySelector(".timeslotButton")
			firstTimeSlot && firstTimeSlot.focus()
			setShouldFocusFirstTimeslot(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTbKind, shouldFocusFirstTimeslot])

	useEffect(() => {
		if (timeblockData) {
			let loungerIsRequired = false
			let loungerNotAvailable = false
			let wholeDayAvailable = false
			let otherTicketsAvailable = false

			let dateIsBefore = false
			let isClosed = false
			let allTicketsAvailable = false
			let wholeDayEntry = false
			let dayTb = undefined

			timeblockData.forEach(e => {
				let temp = e.timeBlocks.find(a => a.timeBlockTypeId === TICKET_TYPES.WholeDay)

				if (temp && !dayTb) {
					dayTb = temp
				}
			})

			if (dayTb) {
				const restrictions = dayTb.items[0].restrictions
				if (restrictions) {
					loungerIsRequired = restrictions.objectRequired
					loungerNotAvailable = restrictions.objectDenied
				}
				//Show message when daytime tickets are available but without any lounger
				const loungerCapacity = dayTb.items[0].objectCapacity
				if (loungerCapacity === 0) {
					loungerNotAvailable = true
				}
			}
			wholeDayEntry = timeblockData.find(e => e.timeBlocks.filter(a => a.timeBlockTypeId === TICKET_TYPES.WholeDay).length > 0)
			if (wholeDayEntry) {
				setOpeningHoursObj(wholeDayEntry.timeBlocks[0].timeSlots[0])
			} else if (timeblockData[0]) {
				let openingHoursObj = getOpeningHours(timeblockData[0])
				setOpeningHoursObj(openingHoursObj.timeBlocks[0].timeSlots[0])
			} else {
				setOpeningHoursObj(undefined)
			}

			// let notifications = timeblockData.find((e) => e.timeBlocks.filter((a) => a.notifications).length > 0);
			// if (notifications) {
			//     setNotifications(notifications.timeBlocks[0].notifications);
			// } else {
			//     setNotifications(undefined);
			// }

			wholeDayAvailable = wholeDayEntry && wholeDayEntry.timeBlocks.filter(e => e.restCount > -1 && e.timeSlots.length).length ? true : false
			otherTicketsAvailable =
				timeblockData
					.filter(e => e.timeBlocks.filter(a => a.timeBlockTypeId !== TICKET_TYPES.WholeDay).length)
					.filter(e => e.timeBlocks.filter(tb => tb.restCount > 0 && tb.timeSlots.length).length).length > 0
					? true
					: false
			allTicketsAvailable = wholeDayAvailable && otherTicketsAvailable
			let d1 = new Date(selectedDay.dateObj),
				d2 = new Date()
			dateIsBefore = compareDate(d1, d2) === -1
			isClosed = selectedDay.exists === false

			let message = undefined
			if (allTicketsAvailable && !loungerIsRequired) {
				message = appStore.get("core.kioskMode") !== true ? dateSelectionLoungerOnlyWithDay : ""
			}
			if (loungerIsRequired) {
				message = appStore.get("core.kioskMode") !== true ? dateSelectionDayWithLoungerOrOther : ""
			}
			if (loungerNotAvailable) {
				message = appStore.get("core.kioskMode") !== true ? dateSelectionDayWithoutLoungerOrOther : ""
			}
			//message takes only effect if the is an exiting whole Day ticket
			if (!wholeDayAvailable && wholeDayEntry) {
				message = dateSelectionOnlyOther
			}
			if (loungerIsRequired && !otherTicketsAvailable) {
				message = appStore.get("core.kioskMode") !== true ? dateSelectionOnldyDayWithLounger : ""
			}
			if (wholeDayAvailable && !otherTicketsAvailable && loungerNotAvailable) {
				message = dateSelectionOnlyDay
			}
			if (!wholeDayAvailable && !otherTicketsAvailable) {
				message = dateSelectionNoTicket
			}

			if (isClosed || (selectedDay && selectedDay.message && selectedDay.message.startsWith("wrn.TimePlanning.NotFound"))) {
				const calendardata = appStore.get("order.calendarData")

				let filtered = [...calendardata]

				let cntCalendarData = calendardata.length

				while (cntCalendarData--) {
					if (calendardata[cntCalendarData].message === "wrn.TimePlanning.NotFound") {
						filtered.splice(cntCalendarData, 1)
					} else {
						break
					}
				}
				/**
				 * Only show the closed message if the selecteday day was in an unplanned period. If the day is not plannend but in the future the are planning
				 * we will only show the notifications
				 */
				if (filtered.find(e => e.dateString === selectedDay.date)) {
					if (selectedDay.notifications?.length) {
						message = undefined
					} else {
						message = dateSelectionIsClosed
					}
				} else {
					message = dateSelectionIsClosed
				}
			}

			if (dateIsBefore) {
				message = dateSelectionIsBefore
			}

			if (selectedDay.message && selectedDay.message.startsWith("wrn.VisitorKind.NoAccess")) {
				message = dateSelectionAccessDenied
			}

			setMessage(message)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeblockData])

	if (timeblockError) {
		return <RetryError loading={__loadingTimeblocks} show onRetry={fetchTimeblocks} />
	}
	if (selectedDay) {
		return (
			<div role="region" aria-label={ticketTypeSelectionlabel} className="py-4 px-3 fill" ref={tickedContainerHandler}>
				<div id="date-selection-ticket-title" className="f3 mb-3 mt-2">
					{dateSelectionTicketTitle}
				</div>
				<div className="f4 mt-3 color-fg-secondary mb-3">{loadingTimeblocks ? <Skeleton count={3} /> : (message ?? "")}</div>

				<div role="region" aria-label={dateNotificationsLabel}>
					{notifications &&
						notifications.map((notification, index) => (
							<div
								key={index}
								className={"notification f5 mt-3 color-fg-default color-bg-red-100 mb-3 p-1 flex" + classNamez({ clickable: notification.memo })}
								tabIndex={notification.memo ? 0 : -1}
								role={notification.memo && "button"}
								aria-label={notification.memo && notification.notification + moreInformation}
								onClick={() => {
									handleNotificationInfoPress(notification)
								}}
								onKeyDown={e => {
									if (elementWasSelected(e)) {
										e.preventDefault()
										handleNotificationInfoPress(notification)
									}
								}}
							>
								<div>{notification.notification}</div>
								{notification.memo && (
									<div className="pl-2">
										<FontAwesomeIcon icon={faInfoCircle} />
									</div>
								)}
							</div>
						))}
				</div>
				<div style={{ minHeight: "70px", overflowX: "auto" }}>
					{loadingTimeblocks && (
						<div className="flex flex-justify-between fill">
							<div className="f00 flex-1">
								<Skeleton />
							</div>
							<div className="f00 ml-4 flex-1" style={{ width: "200px" }}>
								<Skeleton />
							</div>
						</div>
					)}

					{timeblockData && !loadingTimeblocks && selectedDay.valid && (
						<div className="flex flex-justify-between fill tbButtons px-1" role="region" aria-label={timeblockSelectionLabel}>
							{timeblockData.map((tb, index) => {
								let disabled = tb.timeBlocks.filter(e => e.restCount > -1 && e.timeSlots.length).length === 0 ? true : false

								if (appStore.get("core.kioskMode")) {
									if (tb.timeBlocks && tb.timeBlocks[0] && tb.timeBlocks[0].items && tb.timeBlocks[0].items[0]) {
										let isRestriced = tb.timeBlocks[0].items[0].restrictions?.objectRequired
										if (isRestriced) {
											disabled = true
										}
									}
								}
								//if no timeblock has a restCount we show a different message then disabled
								let noRestCount = tb?.timeBlocks.every(e => e.restCount == null)

								if (selectedDay?.price == null || selectedDay?.restCount == null) {
									return
								}

								let hasDiff = hasDifferentPrice(tb)
								return (
									<TimeblockButton
										key={index}
										type={tb.type}
										price={tb.minPrice}
										showFrom={hasDiff}
										onClick={() => setSelectedTimeblockKind(tb)}
										onKeyDown={_e => {
											setSelectedTimeblockKind(tb)
											setShouldFocusFirstTimeslot(true)
										}}
										selected={selectedTbKind && selectedTbKind.type == tb.type}
										disabled={disabled}
										noRestCount={noRestCount}
									/>
								)
							})}
						</div>
					)}
				</div>

				{selectedTbKind && selectedTbKind.timeBlocks[0].timeBlockTypeId !== TICKET_TYPES.WholeDay && (
					<div role="region" aria-label={timeslotSelectionLabel}>
						<div className="f3 mt-5">{dateSelectionTimeslotTitle}</div>
						<div className="f4 mt-2 color-fg-secondary">{dateSelectionTimeslotSubTitle}</div>
						<div className="mt-4">
							<div className="flex flex-wrap mt-2 ">
								{selectedTbKind.timeBlocks.map(tb => {
									let isDiff = tb.price !== selectedTbKind.timeBlocks[0].price
									if (tb.restCount < 0) {
										return null
									}
									return tb.timeSlots.map((ts, index) => {
										return (
											<TimeslotButton
												key={index}
												onClick={() => setSelectedTimeslot(ts, tb)}
												selected={selectedTs == ts}
												time={ts.timeFrom}
												discount={isDiff}
											/>
										)
									})
								})}
							</div>
						</div>
					</div>
				)}

				{selectedTbKind && hasDifferentPrice(selectedTbKind) && <div className="color-fg-yellow-700">{dateSelectionEveningTicketNote}</div>}

				{selectedDay.valid &&
					(!selectedTbKind || (selectedTbKind && selectedTbKind.timeBlocks[0].timeBlockTypeId === TICKET_TYPES.WholeDay)) &&
					openingHoursObj && (
						<div className="f4 mt-3">
							{dateSelectionYourVisitTime}
							<span className="color-fg-yellow-700">
								{" "}
								{from} {openingHoursObj.timeFrom.substring(0, openingHoursObj.timeFrom.length - 3)} {to}{" "}
								{openingHoursObj.timeTo.substring(0, openingHoursObj.timeTo.length - 3)}
							</span>
						</div>
					)}
			</div>
		)
	}

	return null
}
